import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { Brain, MessageCircle, Wand2 } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

interface AISettings {
  responseLength: "short" | "medium" | "long"
  assistantType: "helpful" | "creative" | "professional" | "friendly"
  conversationalTone: boolean
  temperature: number[]
}

const defaultSettings: AISettings = {
  responseLength: "medium",
  assistantType: "helpful",
  conversationalTone: true,
  temperature: [0.3],
}

export function AISettings({ chatbotId }: { chatbotId?: string }) {
  const [settings, setSettings] = useState<AISettings>(defaultSettings)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Fetch current chatbot settings
  const { data: chatbot } = useQuery({
    queryKey: ['chatbot', chatbotId],
    queryFn: async () => {
      if (!chatbotId) return null
      const { data, error } = await supabase
        .from('chatbots')
        .select('*')
        .eq('id', chatbotId)
        .single()
      
      if (error) throw error
      return data
    },
    enabled: !!chatbotId,
    meta: {
      onSettled: (data: any) => {
        console.log("Fetched chatbot data:", data)
        if (data?.temperature) {
          setSettings(prev => ({
            ...prev,
            temperature: [data.temperature]
          }))
        }
      }
    }
  })

  const updateChatbot = useMutation({
    mutationFn: async (updates: { temperature: number }) => {
      console.log("Updating chatbot with temperature:", updates.temperature)
      if (!chatbotId) throw new Error("No chatbot selected")
      const { error } = await supabase
        .from('chatbots')
        .update(updates)
        .eq('id', chatbotId)
      
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot', chatbotId] })
      toast({
        title: "Settings saved",
        description: "Your AI settings have been updated successfully."
      })
      setIsLoading(false)
    },
    onError: (error) => {
      console.error('Error saving settings:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings. Please try again."
      })
      setIsLoading(false)
    }
  })

  const handleSave = async () => {
    try {
      setIsLoading(true)
      console.log("Saving settings:", settings)
      
      // Save to local storage for non-chatbot settings
      localStorage.setItem("aiSettings", JSON.stringify({
        ...settings,
        temperature: settings.temperature[0],
      }))
      
      // If we have a chatbot ID, update the chatbot settings in Supabase
      if (chatbotId) {
        await updateChatbot.mutateAsync({
          temperature: settings.temperature[0]
        })
      } else {
        setIsLoading(false)
        toast({
          title: "Settings saved",
          description: "Your AI settings have been saved locally."
        })
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings. Please try again."
      })
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Settings</CardTitle>
          <CardDescription>
            Configure how the AI assistant behaves and responds
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label>Assistant Type</Label>
            <Select
              value={settings.assistantType}
              onValueChange={(value: "helpful" | "creative" | "professional" | "friendly") =>
                setSettings(prev => ({ ...prev, assistantType: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="helpful">
                  <div className="flex items-center">
                    <Brain className="h-4 w-4 mr-2" />
                    Helpful
                  </div>
                </SelectItem>
                <SelectItem value="creative">
                  <div className="flex items-center">
                    <Wand2 className="h-4 w-4 mr-2" />
                    Creative
                  </div>
                </SelectItem>
                <SelectItem value="professional">
                  <div className="flex items-center">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Professional
                  </div>
                </SelectItem>
                <SelectItem value="friendly">
                  <div className="flex items-center">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Friendly
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <Label>Response Length</Label>
            <RadioGroup
              value={settings.responseLength}
              onValueChange={(value: "short" | "medium" | "long") =>
                setSettings(prev => ({ ...prev, responseLength: value }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="short" id="short" />
                <Label htmlFor="short">Short</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="medium" id="medium" />
                <Label htmlFor="medium">Medium</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="long" id="long" />
                <Label htmlFor="long">Long</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <Label>Temperature (Creativity vs Consistency)</Label>
            <Input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={settings.temperature[0]}
              onChange={(e) =>
                setSettings(prev => ({
                  ...prev,
                  temperature: [parseFloat(e.target.value)]
                }))
              }
            />
            <div className="text-sm text-muted-foreground">
              {settings.temperature[0]} - {settings.temperature[0] === 0
                ? "More focused and deterministic"
                : settings.temperature[0] === 1
                ? "More creative and varied"
                : "Balanced between focus and creativity"}
            </div>
          </div>

          <Button
            onClick={handleSave}
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Settings"}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}